import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useStudentHub } from '../contexts/StudentHubContext';
import { Button } from '../components/ui/button';
import { RefreshCw, AlertCircle, ChevronDown } from 'lucide-react';
import { 
  CategoryHeader, 
  CategorySearchBar, 
  BookCard, 
  BookCardSkeleton,
  LevelChipsRow
} from '../components/student-hub';
import BooksFilterSheet, { DEFAULT_FILTERS, countActiveFilters } from '../components/student-hub/BooksFilterSheet';
import BookListCard from '../components/listings/BookListCard';
import '../components/listings/BookListCard.css';
import BookGridCard from '../components/listings/BookGridCard';
import '../components/listings/BookGridCard.css';
import ViewToggle from '../components/ViewToggle';
import '../components/ViewToggle.css';
import useViewPreference from '../hooks/useViewPreference';
import '../components/student-hub/CategoryComponents.css';
import '../components/student-hub/BookCard.css';
import '../components/student-hub/BooksFilterSheet.css';
import '../components/student-hub/LevelChipsRow.css';
import { fetchStudentHubItems } from '../services/studentHubApi';
import { sortAdsByPrice } from '../utils/priceUtils';
import { trackSearch, trackFilterChange, flushTracking, resetViewedItems } from '../utils/tracker';
import useViewTracking from '../hooks/useViewTracking';
import { usePostingChooser } from '../contexts/PostingChooserContext';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import { getApiBase } from '../api/httpClient';
import './StudentHub.css';
import './StudentBooksPage.css';

const SEARCH_DEBOUNCE = 300;

// Content category chips configuration.
// Includes the new posting subtypes (notebook, pdf, magazine). Legacy
// values (lab_manual, slides) remain queryable via the BooksFilterSheet
// — they're just not surfaced as primary chips.
const CONTENT_CATEGORIES = [
  { key: '',              i18nKey: 'sbp.all' },
  { key: 'textbook',      i18nKey: 'sbp.textbooks' },
  { key: 'summary',       i18nKey: 'sbp.summaries' },
  { key: 'notebook',      i18nKey: 'sbp.notebook' },
  { key: 'past_exam',     i18nKey: 'sbp.pastPapers' },
  { key: 'lecture_notes', i18nKey: 'sbp.lectureNotes' },
  { key: 'pdf',           i18nKey: 'sbp.pdf' },
  { key: 'magazine',      i18nKey: 'sbp.magazine' },
];

// Sort options
const SORT_OPTIONS = [
  { key: 'newest', i18nKey: 'sbp.newest' },
  { key: 'price_asc', i18nKey: 'sbp.priceLowHigh' },
  { key: 'price_desc', i18nKey: 'sbp.priceHighLow' },
  { key: 'free_first', i18nKey: 'sbp.freeFirst' },
];

export default function StudentBooksPage() {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const { academicLevel, effectiveLevel, updateAcademicLevel, getLevelLabel } = useStudentHub();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const contentRef = useRef(null);
  const { openChooser } = usePostingChooser();
  
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  const lang = isRTL ? 'ar' : 'en';
  
  // View preference (list/grid toggle) - Books default to list
  const { view: listingView, setView: setListingView } = useViewPreference('books');
  
  // State
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // Content category chip
  const [contentCategory, setContentCategory] = useState(searchParams.get('contentType') || '');
  
  // View tracking hook
  const { cardRef } = useViewTracking('books_notes', books.map(b => b.id));
  
  // Filters state — the "applied" filters (only changes on Apply)
  const [filters, setFilters] = useState({
    mode: searchParams.get('mode') || DEFAULT_FILTERS.mode,
    priceMin: searchParams.get('priceMin') ? parseInt(searchParams.get('priceMin')) : DEFAULT_FILTERS.priceMin,
    priceMax: searchParams.get('priceMax') ? parseInt(searchParams.get('priceMax')) : DEFAULT_FILTERS.priceMax,
    country: searchParams.get('country') || DEFAULT_FILTERS.country,
    city: searchParams.get('city') || DEFAULT_FILTERS.city,
    sort: searchParams.get('sort') || DEFAULT_FILTERS.sort,
  });
  
  // Genre state (persisted in sessionStorage)
  const [genre, setGenre] = useState(() => {
    const urlGenre = searchParams.get('genre');
    if (urlGenre) return urlGenre;
    try {
      return sessionStorage.getItem('studentHub.books.genre') || '';
    } catch {
      return '';
    }
  });
  
  // Refs
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const sortRef = useRef(null);
  
  // Close sort dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Sync level from URL on mount
  useEffect(() => {
    const urlLevel = searchParams.get('level');
    if (urlLevel && urlLevel !== academicLevel) {
      updateAcademicLevel(urlLevel);
    }
  }, []);
  
  // Flush tracking on unmount
  useEffect(() => {
    return () => { flushTracking(); };
  }, []);
  
  // Reset viewed items when level or filters change
  useEffect(() => {
    resetViewedItems();
  }, [effectiveLevel, filters]);
  
  // Fetch books
  const fetchBooks = useCallback(async (query, filtersOverride, skipCache = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    const currentFilters = filtersOverride || filters;
    
    try {
      const result = await fetchStudentHubItems({
        category: 'books_notes',
        level: effectiveLevel || undefined,
        genre: genre || undefined,
        q: query || undefined,
        mode: currentFilters.mode || undefined,
        priceMin: currentFilters.priceMin,
        priceMax: currentFilters.priceMax,
        city: currentFilters.city || undefined,
        country: currentFilters.country || undefined,
        sort: currentFilters.sort,
        limit: 50,
        offset: 0,
        signal: abortControllerRef.current.signal,
        skipCache,
      });
      
      if (isMountedRef.current) {
        let items = result.items || [];
        
        // Client-side content category filter
        if (contentCategory) {
          items = items.filter(book => 
            book.tags?.includes(contentCategory) || book.type === contentCategory
          );
        }
        
        // Sort modes are MUTUALLY EXCLUSIVE.  When the user explicitly picks
        // price_asc or price_desc, free items (price === 0 / mode === 'free')
        // must NOT be hoisted to the top — they sort by their numeric price
        // like everything else.  free_first only fires for the relevance/
        // default mode chosen by the user.
        if (currentFilters.sort === 'free_first') {
          items.sort((a, b) => {
            const aFree = a.mode === 'free' || a.price === 0 ? 0 : 1;
            const bFree = b.mode === 'free' || b.price === 0 ? 0 : 1;
            return aFree - bFree;
          });
        } else if (currentFilters.sort === 'price_asc') {
          // Use shared priceUtils — handles Arabic digits, currency labels,
          // and pushes invalid/missing prices to the END (not 0).
          items = sortAdsByPrice(items, 'asc');
        } else if (currentFilters.sort === 'price_desc') {
          items = sortAdsByPrice(items, 'desc');
        }
        
        setBooks(items);
        setTotal(result.total || 0);
        setError(null);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (isMountedRef.current) {
        setError(err.message || 'Failed to load books');
        setBooks([]);
        setTotal(0);
      }
    }
  }, [filters, effectiveLevel, genre, contentCategory]);
  
  // Initial load
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    
    fetchBooks(searchQuery).finally(() => {
      if (isMountedRef.current) setLoading(false);
    });
    
    if (user && token) fetchSavedItems();
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);
  
  // Refetch when level changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (effectiveLevel) {
      newParams.set('level', effectiveLevel);
    } else {
      newParams.delete('level');
    }
    setSearchParams(newParams, { replace: true });
    
    setLoading(true);
    fetchBooks(searchQuery).finally(() => {
      if (isMountedRef.current) {
        setLoading(false);
        contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }, [effectiveLevel]);
  
  // Refetch when filters or contentCategory change
  useEffect(() => {
    setLoading(true);
    fetchBooks(searchQuery).finally(() => {
      if (isMountedRef.current) setLoading(false);
    });
  }, [filters.mode, filters.priceMin, filters.priceMax, filters.city, filters.country, filters.sort, genre, contentCategory]);
  
  // Debounced search
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setLoading(true);
      fetchBooks(value).finally(() => {
        if (isMountedRef.current) setLoading(false);
      });
    }, SEARCH_DEBOUNCE);
  }, [fetchBooks]);
  
  // Search submit
  const handleSearchSubmit = useCallback((term) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const newParams = new URLSearchParams(searchParams);
    if (term) newParams.set('q', term);
    else newParams.delete('q');
    setSearchParams(newParams);
    
    if (term?.trim().length > 0) {
      trackSearch(term.trim(), 'books_notes', filters);
    }
    
    setLoading(true);
    fetchBooks(term).finally(() => {
      if (isMountedRef.current) setLoading(false);
    });
  }, [searchParams, setSearchParams, fetchBooks, filters]);
  
  // Retry
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchBooks(searchQuery, filters, true).finally(() => {
      if (isMountedRef.current) setLoading(false);
    });
  }, [searchQuery, filters, fetchBooks]);

  // ═══════════════════════════════════════════════════════════
  // UNIFIED SEARCH PIPELINE — single source of truth
  // normalize → filter → dedupe (shared with Home, Shop, etc.)
  // ═══════════════════════════════════════════════════════════
  // iter298 fix — `_fetch_user_book_listings_as_hub_items` transforms
  // each row to `{id: book.book_id, ...}` (NOT book_id). Passing
  // `idKey: 'book_id'` made dedupeAdsById drop every row because
  // book_id was undefined. Use the default (ad.ad_id || ad.id) so the
  // pipeline picks up the canonical `id` field.
  const displayBooks = useUnifiedSearch(books, searchQuery);

  // Saved items
  const fetchSavedItems = async () => {
    try {
      const res = await fetch(`${getApiBase()}/api/saved-items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedItems(data.saved_items || []);
      }
    } catch (e) { console.error(e); }
  };
  
  const handleSave = async (bookId) => {
    if (!user || !token) { navigate('/welcome'); return; }
    const isSaved = savedItems.some(s => s.item_type === 'book' && s.item_id === bookId);
    try {
      if (isSaved) {
        await fetch(`${getApiBase()}/api/saved-items/book/${bookId}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        setSavedItems(prev => prev.filter(s => !(s.item_type === 'book' && s.item_id === bookId)));
      } else {
        await fetch(`${getApiBase()}/api/saved-items`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_type: 'book', item_id: bookId })
        });
        setSavedItems(prev => [...prev, { item_type: 'book', item_id: bookId }]);
      }
    } catch (e) { console.error(e); }
  };
  
  const isBookSaved = (bookId) => savedItems.some(s => s.item_type === 'book' && s.item_id === bookId);
  
  const handleBack = () => {
    if (hasInAppHistory()) return navigate(-1);
    navigate(location.state?.from || '/student-hub', { replace: true });
  };
  
  const handleCTAClick = (book) => {
    // Navigate to book details page for all CTA actions
    navigate(`/student-hub/books/${book.id}`, { state: { book } });
  };
  
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    trackFilterChange('books_notes', {
      mode: newFilters.mode || null,
      priceMin: newFilters.priceMin,
      priceMax: newFilters.priceMax,
      country: newFilters.country || null,
      city: newFilters.city || null,
      sort: newFilters.sort,
      level: effectiveLevel || null,
    });
    
    const newParams = new URLSearchParams(searchParams);
    if (newFilters.mode) newParams.set('mode', newFilters.mode);
    else newParams.delete('mode');
    if (newFilters.priceMin !== undefined) newParams.set('priceMin', newFilters.priceMin.toString());
    else newParams.delete('priceMin');
    if (newFilters.priceMax !== undefined) newParams.set('priceMax', newFilters.priceMax.toString());
    else newParams.delete('priceMax');
    if (newFilters.country) newParams.set('country', newFilters.country);
    else newParams.delete('country');
    if (newFilters.city) newParams.set('city', newFilters.city);
    else newParams.delete('city');
    if (newFilters.sort && newFilters.sort !== 'newest') newParams.set('sort', newFilters.sort);
    else newParams.delete('sort');
    setSearchParams(newParams, { replace: true });
  };
  
  const handleGenreChange = useCallback((newGenre) => {
    setGenre(newGenre);
    try {
      if (newGenre) sessionStorage.setItem('studentHub.books.genre', newGenre);
      else sessionStorage.removeItem('studentHub.books.genre');
    } catch {}
    const newParams = new URLSearchParams(searchParams);
    if (newGenre) newParams.set('genre', newGenre);
    else newParams.delete('genre');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);
  
  // Sort handler
  const handleSortChange = (sortKey) => {
    setFilters(prev => ({ ...prev, sort: sortKey }));
    setShowSortDropdown(false);
  };
  
  // Content category handler
  const handleContentCategoryChange = (key) => {
    setContentCategory(key);
    const newParams = new URLSearchParams(searchParams);
    if (key) newParams.set('contentType', key);
    else newParams.delete('contentType');
    setSearchParams(newParams, { replace: true });
  };
  
  // Active filter count — derived from APPLIED filters only (not draft)
  const activeFilterCount = countActiveFilters(filters);
  
  const handleLevelChange = useCallback(() => {
    navigate('/student-hub', { state: { showLevelPicker: true } });
  }, [navigate]);
  
  // Render helpers
  const renderSkeletons = () => (
    <div className="books-grid-v2">
      {[1, 2, 3, 4, 5, 6].map(i => <BookCardSkeleton key={i} />)}
    </div>
  );
  
  const renderError = () => (
    <div className="error-state" data-testid="error-state">
      <AlertCircle size={48} className="error-icon" />
      <p className="error-message">{error}</p>
      <Button variant="outline" onClick={handleRetry} className="retry-btn">
        <RefreshCw size={16} />
        {t('had.retry')}
      </Button>
    </div>
  );
  
  const hasActiveFilters = activeFilterCount > 0 || searchQuery;
  
  const renderEmpty = () => {
    const hasLocationFilter = filters.country || filters.city;
    return (
      <div className="empty-state" data-testid="empty-state">
        <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="empty-icon">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        </svg>
        <p className="empty-title">
          {/* iter293 — Operator-spec'd empty-state copy. No more
              concatenation of an Arabic level label inside an English
              sentence ("No books for جميع المراحل level"). When no
              filter is active we say a clean single-language sentence
              for each language. */}
          {hasLocationFilter
            ? (isRTL ? 'لا توجد كتب في هذا الموقع' : 'No books found in this location')
            : hasActiveFilters
              ? (isRTL ? 'لا توجد نتائج مطابقة' : 'No matching results')
              : (isRTL
                  ? 'لا توجد كتب لهذه المرحلة'
                  : 'No books for this level')}
        </p>
        <p className="empty-subtitle">
          {hasLocationFilter
            ? (isRTL ? 'حاول تغيير الموقع أو مسح الفلاتر أو أضف إعلاناً جديداً' : 'Try changing the location, clearing filters, or post a new listing')
            : hasActiveFilters
              ? (isRTL ? 'حاول تغيير الفلاتر أو البحث' : 'Try changing your filters or search')
              : (isRTL
                  ? 'جرّب مرحلة أخرى أو كن أول من ينشر كتاباً'
                  : 'Try another level or post the first book')}
        </p>
        <div className="empty-actions">
          {hasActiveFilters && (
            <Button variant="outline" onClick={() => { setFilters({ ...DEFAULT_FILTERS }); setSearchQuery(''); }} className="change-level-btn" data-testid="empty-clear-filters">
              {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
            </Button>
          )}
          {!hasActiveFilters && academicLevel && (
            <Button variant="outline" onClick={handleLevelChange} className="change-level-btn">
              {t('sbp.changeLevel')}
            </Button>
          )}
          <Button variant="outline" onClick={() => openChooser('book')} className="change-level-btn" data-testid="empty-post-listing">
            {isRTL ? 'انشر كتاباً' : 'Post a book'}
          </Button>
        </div>
      </div>
    );
  };
  
  const currentSortLabel = SORT_OPTIONS.find(s => s.key === filters.sort)?.i18nKey || 'sbp.newest';
  
  return (
    <div className="student-books-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="student-books-page">
      {/* Header */}
      <CategoryHeader
        icon="📚"
        title={{ en: 'Books', ar: 'كتب وملخصات' }}
        subtitle={{ en: 'Buy • Rent • Download', ar: 'شراء • إيجار • تحميل' }}
        onBack={handleBack}
        showLevel={false}
      />
      
      {/* Search Bar */}
      <CategorySearchBar
        category="books"
        placeholder={{ en: 'Search books, notes...', ar: 'ابحث عن كتب، ملخصات...' }}
        value={searchQuery}
        onChange={handleSearchChange}
        onSubmit={handleSearchSubmit}
        onFilterClick={() => setShowFilters(true)}
        hasActiveFilters={activeFilterCount > 0}
      />
      
      {/* Level Chips Row */}
      <LevelChipsRow />
      
      {/* #10 — Content Category Chips */}
      <div className="content-category-bar" data-testid="content-category-bar">
        <div className="content-category-scroll">
          {CONTENT_CATEGORIES.map(cat => (
            <button
              key={cat.key}
              className={`content-chip ${contentCategory === cat.key ? 'active' : ''}`}
              onClick={() => handleContentCategoryChange(cat.key)}
              data-testid={`content-chip-${cat.key || 'all'}`}
            >
              {t(cat.i18nKey)}
            </button>
          ))}
        </div>
      </div>
      
      {/* Filter Bottom Sheet */}
      <BooksFilterSheet
        open={showFilters}
        onOpenChange={setShowFilters}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={() => {
          contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
      
      {/* Results */}
      <div className="books-content" ref={contentRef}>
        {/* Row 1: Count + Post CTA */}
        {!loading && !error && displayBooks.length > 0 && (
          <div className="results-action-row" data-testid="results-action-row">
            <span className="results-count-v2">
              {isRTL ? `${displayBooks.length} كتاب` : `${displayBooks.length} books`}
            </span>
            <span className="books-posting-hint" data-testid="books-posting-hint">
              <span className="books-posting-hint-text">
                {isRTL ? 'لديك كتاب؟' : 'Have a book?'}
              </span>
              <button
                className="books-posting-hint-link"
                onClick={() => openChooser('book')}
                data-testid="books-posting-hint-link"
              >
                {isRTL ? 'أضفه هنا' : 'Post it'}
              </button>
            </span>
          </div>
        )}

        {/* Row 2: View Toggle + Sort */}
        {!loading && !error && displayBooks.length > 0 && (
          <div className="results-header-v2" data-testid="results-header">
            {/* View Toggle */}
            <ViewToggle
              view={listingView}
              onViewChange={setListingView}
              size="sm"
              disabled={loading}
            />
            
            {/* Sort dropdown */}
            <div className="sort-dropdown-wrap" ref={sortRef}>
              <button
                className="sort-trigger"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                data-testid="sort-trigger"
              >
                <span>{t(currentSortLabel)}</span>
                <ChevronDown size={14} className={showSortDropdown ? 'rotated' : ''} />
              </button>
              
              {showSortDropdown && (
                <div className="sort-dropdown" data-testid="sort-dropdown">
                  {SORT_OPTIONS.map(opt => (
                    <button
                      key={opt.key}
                      className={`sort-option ${filters.sort === opt.key ? 'active' : ''}`}
                      onClick={() => handleSortChange(opt.key)}
                      data-testid={`sort-${opt.key}`}
                    >
                      {t(opt.i18nKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {loading ? (
          renderSkeletons()
        ) : error ? (
          renderError()
        ) : displayBooks.length === 0 ? (
          renderEmpty()
        ) : listingView === 'list' ? (
          /* List View */
          <div className="book-list-view books-view-container">
            {displayBooks.map(book => (
              <BookListCard
                key={book.id}
                book={book}
                isSaved={isBookSaved(book.id)}
                onSave={handleSave}
                isRTL={isRTL}
                lang={lang}
              />
            ))}
          </div>
        ) : (
          /* Grid View - Simplified cards */
          <div className="book-grid-view books-view-container">
            {displayBooks.map(book => (
              <BookGridCard
                key={book.id}
                book={book}
                isSaved={isBookSaved(book.id)}
                onSave={handleSave}
                isRTL={isRTL}
                lang={lang}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* FAB removed - Using global orange FAB instead */}
    </div>
  );
}
